// Hook that auto-detects the user's phone model and focal length via WURFL.js (loaded dynamically).
// Looks up the detected brand/model in DevicesList to find the camera focal length.
// Falls back to manual selection if WURFL doesn't find a match in DevicesList.

import React, { useEffect } from "react";
import posthog from "../utils/posthog";
import DevicesList from "../utils/deviceFocalLengthJson";

interface DeviceInfo {
  brandName: string;
  modelName: string;
  focalLength: number;
}

interface UsePhoneDetectionParams {
  onDeviceDetected?: (deviceInfo: { brandName: string; modelName: string; focalLength: number }) => void;
}

function usePhoneDetection({
  onDeviceDetected,
}: UsePhoneDetectionParams): null {
  const handleWURFLDetectionComplete = () => {
    try {
      const wurfl = window?.WURFL;
      if (!wurfl) return;

      let phoneModel: { model_name: string; focal_length: number } | undefined;

      if (wurfl.brand_name === "Apple") {
        // WURFL returns generic "iPhone" for Apple — use a safe default focal length
        phoneModel = { model_name: "iPhone", focal_length: 23 };
      } else {
        for (const entry of DevicesList) {
          if (wurfl.brand_name in entry) {
            const models = (entry as Record<string, { model_name: string; focal_length: number }[]>)[wurfl.brand_name];
            phoneModel = models.find(
              (el) => el.model_name === wurfl.model_name || el.model_name === wurfl.marketing_name
            );
            if (phoneModel) break;
          }
        }
      }

      if (!phoneModel) return;
      const deviceInfo: DeviceInfo = {
        brandName: wurfl.brand_name,
        modelName: phoneModel.model_name,
        focalLength: phoneModel.focal_length,
      };

      onDeviceDetected?.({
        brandName: deviceInfo.brandName,
        modelName: deviceInfo.modelName,
        focalLength: deviceInfo.focalLength,
      });

        const payload: Record<string, any> = {
          type: "auto",
          data: JSON.stringify(deviceInfo),
          location:"package"
        };
        posthog.capture(`Phone Detection`, payload);
    } catch (error) {
      console.log(error, "wurfl error");
    }
  };

  useEffect(() => {
    try {
      document.addEventListener("WurflJSDetectionComplete", handleWURFLDetectionComplete);
      const script = document.createElement("script");
      script.src = "//wjs.wurflcloud.com/wurfl.js";
      script.async = true;
      document.head.appendChild(script);
    } catch (error) {
      console.log("error in getting device details", error);
    }
    return () => {
      document.removeEventListener("WurflJSDetectionComplete", handleWURFLDetectionComplete);
    };
  }, []);

  return null;
}

export default usePhoneDetection;
